import ResponsiveGridLayout from './ResponsiveGridLayout';
import { GridWrapper } from './styles';
import { DASHBOARD_BREAKPOINTS, DASHBOARD_COLUMNS } from './types';
import { useCallback, useMemo } from 'react';
import { flow, last, sortBy } from 'lodash';
import { toastVariant, useToast } from 'components/ToastProvider';

const ARRANGE_HANDLE_SELECTOR = '.drag-handle, .react-resizable-handle';

const DashboardGrid = ({
  children,
  updateLayouts,
  resizeHandles,
  rowHeight,
  editable,
  canArrange,
  fullWidth,
  arrangeBlockedMessage,
  setBreakpoint,
  setContainerWidth,
}) => {
  const [showToast] = useToast();
  const arrangeBlocked = canArrange && !fullWidth;

  const handleBlockedArrange = useCallback(
    (event) => {
      if (!event.target.closest?.(ARRANGE_HANDLE_SELECTOR)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      showToast({
        message: arrangeBlockedMessage,
        variant: toastVariant.WARNING,
      });
    },
    [arrangeBlockedMessage, showToast]
  );

  // in case we use measureBeforeMount we need to calculate
  // the minimum height of to avoid scroll to top on zero height
  const minHeight = useMemo(() => {
    const layouts = children.map((child) => {
      const props = child.props;

      return {
        h: props['data-qa-dashlet-position-h'],
        i: props['data-qa-dashlet-position-i'],
        w: props['data-qa-dashlet-position-w'],
        x: props['data-qa-dashlet-position-x'],
        y: props['data-qa-dashlet-position-y'],
      };
    });

    const highest = flow(
      (layouts) => sortBy(layouts, [({ y }) => y, ({ h }) => h]),
      last
    )(layouts || []);

    if (!highest || !rowHeight) {
      return 0;
    }

    return (highest.y + highest.h) * rowHeight;
  }, [children, rowHeight]);

  return (
    <GridWrapper
      rowHeight={rowHeight}
      enableTouchActions={!editable}
      minHeight={minHeight || rowHeight}
      onPointerDownCapture={arrangeBlocked ? handleBlockedArrange : undefined}
    >
      <ResponsiveGridLayout
        className="layout"
        key={`responsive-grid-layout-children-${children?.length}`}
        breakpoints={DASHBOARD_BREAKPOINTS}
        cols={DASHBOARD_COLUMNS}
        onBreakpointChange={setBreakpoint}
        isDraggable={editable}
        isResizable={editable}
        compactType="vertical"
        measureBeforeMount
        onDragStop={updateLayouts}
        onResizeStop={updateLayouts}
        draggableCancel=".no-drag"
        draggableHandle=".drag-handle"
        resizeHandles={canArrange ? resizeHandles : []}
        onWidthChange={setContainerWidth}
        ref={(c) => {
          setContainerWidth(c?.elementRef?.current?.offsetWidth);
        }}
        // We can't use the CSS transforms, otherwise the grid item shadows
        // will appear on top of the adjacent dashlets in some cases
        useCSSTransforms={false}
        rowHeight={rowHeight}
        margin={[10, 10]}
      >
        {children}
      </ResponsiveGridLayout>
    </GridWrapper>
  );
};

export default DashboardGrid;
